import { useEffect, useMemo, useRef, useState } from "react";
import { streamChat } from "../lib/streaming";
import { buildDRSystemPrompt, buildSystemPrompt, parseEvalTail } from "./prompt";
import { medianScore, SCORING_SAMPLES } from "./scoring";
import { renderProse } from "./prose";
import type { ChatMessage } from "../types";
import {
  DR_QUESTIONS,
  fieldsAnswered,
  findField,
  findPhase,
  PHASE_ORDER,
  PHASES,
  totalFields,
  type CanvasSession,
  type FieldDef,
  type PhaseCode,
} from "./schema";
import { getCanvas, submitAnswer, submitDR, advancePhase, jumpTo, skipField } from "./api";
import { canSkipField } from "./canSkip";
import { parseNavCommand } from "./navCommands";
import { phaseFieldSummaries } from "./phaseSummary";
import type { FieldSummary } from "./phaseSummary";
import { PhaseTracker } from "./PhaseTracker";
import { DraftBanner } from "./doc-upload/DraftBanner";
import { discardDraft } from "./doc-upload/extract-api";
import { useScoringModel } from "./scoringModel";

export function chatUrl(): string {
  const u = import.meta.env.VITE_CHAT_FUNCTION_URL as string | undefined;
  if (!u) throw new Error("VITE_CHAT_FUNCTION_URL is not set.");
  return u.replace(/\/$/, "");
}

interface Props {
  sessionId: string;
  onSessionUpdated?: () => void;
  onComplete?: () => void;
  onHome?: () => void;
  onPreview?: () => void;
  /** Open the document-upload step mid-build (drafts fill only unanswered fields). */
  onUploadDoc?: () => void;
}

type Phase = "ask" | "streaming" | "evaluated" | "saving" | "phaseSummary";

export function CanvasFlow({ sessionId, onSessionUpdated, onComplete, onHome, onPreview, onUploadDoc }: Props) {
  const scoringModel = useScoringModel();
  const [session, setSession] = useState<CanvasSession | null>(null);
  const [draft, setDraft] = useState("");
  const [streamText, setStreamText] = useState("");
  const [phase, setPhase] = useState<Phase>("ask");
  const [error, setError] = useState<string | null>(null);
  const [evalParsed, setEvalParsed] = useState<ReturnType<typeof parseEvalTail>>(null);
  const [scoreSampling, setScoreSampling] = useState(false);
  const [pushbackPass, setPushbackPass] = useState(0);
  const [phaseSummary, setPhaseSummary] = useState<{ phase: PhaseCode; fields: FieldSummary[] } | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [prefilledOrigin, setPrefilledOrigin] = useState<{
    fieldName: string;
    text: string;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    abortRef.current?.abort();
    setSession(null);
    setDraft("");
    setStreamText("");
    setPhase("ask");
    setError(null);
    setEvalParsed(null);
    (async () => {
      try {
        const s = await getCanvas(sessionId);
        if (!cancelled) setSession(s);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [sessionId]);

  // Reset pushbackPass when field changes
  useEffect(() => {
    if (session && session.currentStage !== "COMPLETE" && session.currentStage !== "DR") {
      setPushbackPass(0);
    }
  }, [session?.currentStage, session?.currentFieldIndex]);

  // Derive the current PHASE_ORDER field name (only when in a phase, not DR/COMPLETE).
  const currentFieldName =
    session &&
    session.currentStage !== "COMPLETE" &&
    session.currentStage !== "DR"
      ? findField(
          session.currentStage as PhaseCode,
          session.currentFieldIndex,
        ).name
      : undefined;
  const sessionDraft = currentFieldName
    ? session?.docUpload?.drafts?.[currentFieldName]
    : undefined;
  const hasActiveDraft = !!sessionDraft && !sessionDraft.discardedAt;

  // Pre-fill the textarea once per field-with-active-draft.
  useEffect(() => {
    if (
      hasActiveDraft &&
      sessionDraft &&
      currentFieldName &&
      draft.trim() === ""
    ) {
      setDraft(sessionDraft.answer);
      setPrefilledOrigin({
        fieldName: currentFieldName,
        text: sessionDraft.answer,
      });
    }
    // Reset prefilledOrigin when navigating to a different field.
    if (prefilledOrigin && prefilledOrigin.fieldName !== currentFieldName) {
      setPrefilledOrigin(null);
    }
  }, [currentFieldName, hasActiveDraft, sessionDraft, draft, prefilledOrigin]);

  if (!session && !error)
    return <div className="canvas canvas--loading"><Glyph /> Loading session…</div>;
  if (error && !session) return <div className="canvas"><div className="banner banner--error">{error}</div></div>;
  if (!session) return null;

  const totalAnswered = fieldsAnswered(session);
  const totalAll = totalFields() + DR_QUESTIONS.length;

  // ── Branch by stage ────────────────────────────────────────────────────────
  if (session.currentStage === "COMPLETE") {
    return (
      <div className="canvas canvas--complete">
        <PhaseTracker session={session} />
        <div className="canvas__panel">
          <p className="canvas__eyebrow">All phases complete</p>
          <h1 className="canvas__display">Canvas assembled.</h1>
          <p className="canvas__lede">
            Run consistency checks and generate your governance report on the next screen.
          </p>
          <footer className="canvas__actions">
            {onHome && (
              <button className="btn btn--ghost" onClick={onHome}>← Home</button>
            )}
            <button className="btn btn--primary" onClick={() => onComplete?.()}>
              Continue to outputs →
            </button>
          </footer>
        </div>
      </div>
    );
  }

  if (session.currentStage === "DR") {
    return (
      <DRFlow
        session={session}
        sessionId={sessionId}
        onHome={onHome}
        onAdvance={async () => {
          const s = await getCanvas(sessionId);
          setSession(s);
          onSessionUpdated?.();
          if (s.currentStage === "COMPLETE") onComplete?.();
        }}
      />
    );
  }

  const phaseCode = session.currentStage as PhaseCode;
  const phaseDef = findPhase(phaseCode);
  const fieldIdx = session.currentFieldIndex;
  const field: FieldDef = findField(phaseCode, fieldIdx);

  const showSkipCta = canSkipField(session, phaseCode, fieldIdx);

  const handleSkip = async () => {
    setPhase("saving");
    try {
      await skipField(sessionId, {
        phase: phaseCode,
        fieldIndex: fieldIdx,
        reason: "solo operator has no team",
      });
      const fresh = await getCanvas(sessionId);
      setSession(fresh);
      setDraft("");
      setStreamText("");
      setEvalParsed(null);
      setPhase("ask");
      onSessionUpdated?.();
    } catch (e) {
      setError((e as Error).message);
      setPhase("ask");
    }
  };

  const startStream = async () => {
    const cmd = parseNavCommand(draft);
    if (cmd) {
      void handleCommand(cmd);
      return;
    }
    if (!draft.trim() || phase === "streaming") return;
    const system = buildSystemPrompt({ phase: phaseCode, fieldIndex: fieldIdx, session, pushbackPass });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStreamText("");
    setEvalParsed(null);
    setScoreSampling(false);
    setPhase("streaming");

    const fieldMessages: ChatMessage[] = [
      {
        role: "user",
        content:
          `Field: ${phaseCode}.${field.name}\n` +
          `User answer:\n"""\n${draft.trim()}\n"""`,
      },
    ];

    let acc = "";
    streamChat({
      url: chatUrl(),
      sessionId,
      system,
      messages: fieldMessages,
      provider: "anthropic",
      model: scoringModel,
      signal: ctrl.signal,
      onText: (delta) => {
        acc += delta;
        // Render text *without* the structured tail
        const tailIdx = acc.indexOf("<<<JSON>>>");
        setStreamText(tailIdx >= 0 ? acc.slice(0, tailIdx) : acc);
      },
      onDone: () => {
        const parsed = parseEvalTail(acc);
        if (!parsed) {
          setError("Could not parse evaluation. Try refining.");
          setPhase("ask");
          return;
        }
        setStreamText(parsed.proseText);
        setEvalParsed(parsed);
        setPhase("evaluated");
        // Median-of-N: draw extra independent scores and settle on the median,
        // damping the run-to-run scoring wobble before the score is savable.
        if (SCORING_SAMPLES > 1) {
          setScoreSampling(true);
          void medianScore(parsed.score, {
            url: chatUrl(),
            sessionId,
            system,
            messages: fieldMessages,
            model: scoringModel,
            signal: ctrl.signal,
          }).then(({ score }) => {
            setScoreSampling(false);
            setEvalParsed({ ...parsed, score });
            if (score < 4) setPushbackPass((p) => Math.min(3, p + 1));
          });
        } else if (parsed.score < 4) {
          setPushbackPass((p) => Math.min(3, p + 1));
        }
      },
      onError: (e) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setPhase("ask");
      },
    });
  };

  const handleCommand = async (cmd: ReturnType<typeof parseNavCommand>) => {
    if (cmd === "go_back") {
      if (fieldIdx > 0) {
        try {
          await jumpTo(sessionId, { phase: phaseCode, fieldIndex: fieldIdx - 1 });
          const fresh = await getCanvas(sessionId);
          setSession(fresh);
          setDraft("");
          setStreamText("");
          setEvalParsed(null);
          setPhase("ask");
        } catch (e) {
          setError((e as Error).message);
        }
      } else if (PHASE_ORDER.indexOf(phaseCode) > 0) {
        const prevPhase = PHASE_ORDER[PHASE_ORDER.indexOf(phaseCode) - 1];
        const prevDef = findPhase(prevPhase);
        try {
          await jumpTo(sessionId, { phase: prevPhase, fieldIndex: prevDef.fields.length - 1 });
          const fresh = await getCanvas(sessionId);
          setSession(fresh);
          setDraft("");
          setStreamText("");
          setEvalParsed(null);
          setPhase("ask");
        } catch (e) {
          setError((e as Error).message);
        }
      } else {
        setDraft("");
        setError("Already at the first field; cannot go back.");
      }
    } else if (cmd === "progress") {
      setProgressOpen(true);
      setDraft("");
    } else if (cmd === "save_and_quit") {
      setDraft("");
      onSessionUpdated?.();
      window.location.href = "/"; // back to dashboard
    } else if (cmd === "skip") {
      if (showSkipCta) {
        await handleSkip();
      } else {
        setError("Skip is only available for solo_operator + S1.human_team_foundation. Current (complexity, phase, field) does not qualify.");
      }
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    setPhase("ask");
  };

  const handleSaveAndNext = async (advance: boolean) => {
    if (!evalParsed || !session) return;
    setPhase("saving");
    try {
      const isLastFieldOfPhase = fieldIdx === findPhase(phaseCode).fields.length - 1;
      const source: "document_upload" | "document_upload_edited" | undefined =
        prefilledOrigin && prefilledOrigin.fieldName === currentFieldName
          ? draft.trim() === prefilledOrigin.text.trim()
            ? "document_upload"
            : "document_upload_edited"
          : undefined;
      await submitAnswer(sessionId, {
        phase: phaseCode,
        fieldIndex: fieldIdx,
        answer: draft.trim(),
        evalText: evalParsed.proseText,
        score: evalParsed.score,
        gapFlagged: evalParsed.gapFlagged,
        gapNote: evalParsed.gapNote,
        confidence: evalParsed.confidence,
        advance: advance && !isLastFieldOfPhase, // suppress advance on last field — show summary first
        pushbackPass,
        source,
        scoringModel,
      });
      const fresh = await getCanvas(sessionId);
      setSession(fresh);

      if (advance && isLastFieldOfPhase) {
        // Show the phase-end summary; user confirms or revises before phase advances.
        const summaries = phaseFieldSummaries(fresh, phaseCode);
        setPhaseSummary({ phase: phaseCode, fields: summaries });
        setPhase("phaseSummary");
        setDraft("");
        setStreamText("");
        setEvalParsed(null);
        setPrefilledOrigin(null);
      } else {
        setDraft("");
        setStreamText("");
        setEvalParsed(null);
        setPrefilledOrigin(null);
        setPhase("ask");
        onSessionUpdated?.();
        if (fresh.currentStage === "COMPLETE") onComplete?.();
      }
    } catch (e) {
      setError((e as Error).message);
      setPhase("evaluated");
    }
  };

  const handleAcceptAsGap = async () => {
    if (!evalParsed || !session) return;
    setPhase("saving");
    try {
      await submitAnswer(sessionId, {
        phase: phaseCode,
        fieldIndex: fieldIdx,
        answer: draft.trim(),
        evalText: evalParsed.proseText,
        score: evalParsed.score,
        gapFlagged: true,
        gapNote: evalParsed.gapNote || "Accepted at Pass 3 — open governance item",
        confidence: evalParsed.confidence,
        advance: true,
        pushbackPass,
        acceptedAsGap: true,
        scoringModel,
      });
      const fresh = await getCanvas(sessionId);
      setSession(fresh);
      setDraft("");
      setStreamText("");
      setEvalParsed(null);
      setPhase("ask");
      onSessionUpdated?.();
      if (fresh.currentStage === "COMPLETE") onComplete?.();
    } catch (e) {
      setError((e as Error).message);
      setPhase("evaluated");
    }
  };

  const handleConfirmPhase = async () => {
    if (!phaseSummary) return;
    setPhase("saving");
    try {
      await advancePhase(sessionId, { phase: phaseSummary.phase });
      const fresh = await getCanvas(sessionId);
      setSession(fresh);
      setPhaseSummary(null);
      setPhase("ask");
      onSessionUpdated?.();
      if (fresh.currentStage === "COMPLETE") onComplete?.();
    } catch (e) {
      setError((e as Error).message);
      setPhase("phaseSummary");
    }
  };

  const handleRevisePhaseField = async (fieldName: string) => {
    if (!phaseSummary) return;
    const phaseDef = findPhase(phaseSummary.phase);
    const idx = phaseDef.fields.findIndex((f) => f.name === fieldName);
    if (idx < 0) return;
    setPhase("saving");
    try {
      await jumpTo(sessionId, { phase: phaseSummary.phase, fieldIndex: idx });
      const fresh = await getCanvas(sessionId);
      setSession(fresh);
      setPhaseSummary(null);
      setPhase("ask");
    } catch (e) {
      setError((e as Error).message);
      setPhase("phaseSummary");
    }
  };

  const handleRefine = () => {
    setStreamText("");
    setEvalParsed(null);
    setPhase("ask");
  };

  return (
    <div className="canvas">
      <PhaseTracker session={session} />

      <div className="canvas__panel">
        <header className="canvas__header">
          <div className="canvas__meta">
            <span className="canvas__phase-code">{phaseCode}</span>
            <span className="canvas__phase-title">— {phaseDef.title}</span>
          </div>
          <div className="canvas__progress">
            <span className="mono">{fieldIdx + 1}/{phaseDef.fields.length}</span>
            <span className="canvas__progress-sep">·</span>
            <span className="mono">{totalAnswered}/{totalAll} total</span>
            {onHome && (
              <>
                <span className="canvas__progress-sep">·</span>
                <button className="btn btn--text" onClick={onHome}>← Home</button>
              </>
            )}
            {onPreview && (
              <>
                <span className="canvas__progress-sep">·</span>
                <button className="btn btn--text" onClick={onPreview}>Preview &amp; print</button>
              </>
            )}
          </div>
        </header>

        {phase === "phaseSummary" && phaseSummary && (
          <section className="phase-summary">
            <header className="phase-summary__header">
              <h2>Phase {phaseSummary.phase} complete — review before advancing</h2>
              <p className="phase-summary__sub">
                Confirm everything reads right, or revise any field below.
              </p>
            </header>
            <ol className="phase-summary__fields">
              {phaseSummary.fields.map((f) => (
                <li key={f.name} className={f.gapFlagged ? "phase-summary__field phase-summary__field--gap" : "phase-summary__field"}>
                  <div className="phase-summary__field-head">
                    <span className="phase-summary__field-label">
                      {f.code ? <><span className="mono">{f.code}</span> · {f.label}</> : f.label}
                    </span>
                    <span className="mono">{f.score == null ? "—" : `${f.score}/5`}</span>
                  </div>
                  <p className="phase-summary__field-answer">{f.answer.slice(0, 240)}{f.answer.length > 240 ? "…" : ""}</p>
                  {f.gapFlagged && f.gapNote && <p className="phase-summary__field-gap">Gap: {f.gapNote}</p>}
                  <button className="btn btn--text" onClick={() => void handleRevisePhaseField(f.name)}>Revise this field</button>
                </li>
              ))}
            </ol>
            <footer className="phase-summary__actions">
              <button className="btn btn--primary btn--large" onClick={() => void handleConfirmPhase()}>
                Confirm — move on →
              </button>
            </footer>
          </section>
        )}

        {progressOpen && (
          <div className="modal-backdrop" onClick={() => setProgressOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <header><h2>Progress</h2></header>
              <ol>
                {PHASE_ORDER.map((p) => {
                  const st = session?.phases[p].status ?? "locked";
                  const answered = Object.keys(session?.phases[p].fields ?? {}).length;
                  const total = findPhase(p).fields.length;
                  return (
                    <li key={p}>
                      <span className={`stage stage--${st}`}>{st}</span>
                      <span className="mono">{p}</span>: {answered}/{total} fields
                    </li>
                  );
                })}
                <li>
                  <span className={`stage stage--${session?.deploymentReadiness.status ?? "locked"}`}>{session?.deploymentReadiness.status ?? "locked"}</span>
                  <span className="mono">DR</span>: {Object.keys(session?.deploymentReadiness.answers ?? {}).length}/5 questions
                </li>
              </ol>
              <button className="btn btn--ghost" onClick={() => setProgressOpen(false)}>Close</button>
            </div>
          </div>
        )}

        {!showSkipCta && phase !== "phaseSummary" && (
          <>
            <p className="canvas__eyebrow">
              {field.code ? (
                <>
                  <span className="mono">{field.code}</span> · {field.label}
                </>
              ) : (
                field.label
              )}
            </p>
            <h1 className="canvas__display">{renderProse(field.question)}</h1>
            <p className="canvas__intent">Intent — {renderProse(field.intent)}</p>

            {hasActiveDraft && sessionDraft && session.docUpload && (
              <DraftBanner
                draft={sessionDraft}
                sourceFileName={session.docUpload.sourceFile.name}
                onUse={() => {
                  // Restore the original draft text. Useful after editing —
                  // this is effectively a "revert to draft" affordance.
                  setDraft(sessionDraft.answer);
                  // Briefly flash the textarea so the user sees something
                  // changed even when the value was already the draft.
                  const ta = textareaRef.current;
                  if (ta) {
                    ta.focus();
                    ta.setSelectionRange(ta.value.length, ta.value.length);
                  }
                }}
                onEdit={() => {
                  // Focus the textarea and put the cursor at the end so the
                  // user can immediately start editing.
                  const ta = textareaRef.current;
                  if (ta) {
                    ta.focus();
                    ta.setSelectionRange(ta.value.length, ta.value.length);
                  }
                }}
                onDiscard={async () => {
                  try {
                    await discardDraft(sessionId, currentFieldName!);
                    setDraft("");
                    // Optimistic local update so the banner disappears immediately.
                    setSession((s) =>
                      s && s.docUpload
                        ? {
                            ...s,
                            docUpload: {
                              ...s.docUpload,
                              drafts: {
                                ...s.docUpload.drafts,
                                [currentFieldName!]: {
                                  ...s.docUpload.drafts[currentFieldName!],
                                  discardedAt: Date.now(),
                                },
                              },
                            },
                          }
                        : s,
                    );
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              />
            )}
            <div className="composer">
              <div className="composer__label-row">
                <label className="composer__label" htmlFor="answer">Your answer</label>
                {onUploadDoc && (
                  <button
                    type="button"
                    className="btn btn--text composer__upload"
                    onClick={onUploadDoc}
                    title="Upload a document to pre-fill drafts for fields you haven't answered yet"
                  >
                    ↑ Upload a document
                  </button>
                )}
              </div>
              <textarea
                ref={textareaRef}
                id="answer"
                className="composer__textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void startStream();
                  }
                }}
                placeholder="Be specific. Name owners, mechanisms, cadences. Vague answers will get pushback."
                disabled={phase === "streaming" || phase === "saving"}
                rows={6}
              />
              <div className="composer__hint">
                <span>Cmd/Ctrl + Enter to submit</span>
                {session.pushbackBudgetMode === "critical_only" ? (
                  <span className="mono pushback-chip pushback-chip--suspended" title="60+ min elapsed — pushback suspended for the rest of this session">
                    Pushback suspended (≥60 min)
                  </span>
                ) : session.pushbackBudgetMode === "reduced" ? (
                  <span className="mono pushback-chip pushback-chip--reduced" title="45+ min elapsed — pushback reduced to Pass 1 only">
                    Pushback reduced (≥45 min)
                  </span>
                ) : (
                  <span
                    className={`mono pushback-chip pushback-chip--p${pushbackPass}`}
                    title={
                      pushbackPass === 0
                        ? "No pushback on this field yet — the first submit is the initial ask."
                        : pushbackPass === 1
                          ? "Pass 1: Gentle Probe — coach asks for more specifics."
                          : pushbackPass === 2
                            ? "Pass 2: Example-Anchored Challenge — coach shows what good looks like."
                            : "Pass 3: Governance-Gap Warning — final pushback; revise or accept as gap."
                    }
                  >
                    Pushback pass: {pushbackPass} of 3
                  </span>
                )}
              </div>
              <p className="composer__save-note">
                Saved automatically as you go — nothing you enter is ever lost, and you can export everything anytime.
              </p>
            </div>

            {(phase === "streaming" || phase === "evaluated" || streamText) && (
              <EvaluationCard
                text={streamText}
                streaming={phase === "streaming"}
                parsed={evalParsed}
              />
            )}

            {error && <div className="banner banner--error">{error}</div>}

            <footer className="canvas__actions">
              {phase === "ask" && (
                <button
                  className="btn btn--primary"
                  disabled={!draft.trim()}
                  onClick={() => void startStream()}
                >
                  Submit for evaluation →
                </button>
              )}
              {phase === "streaming" && (
                <button className="btn btn--ghost" onClick={handleStop}>Stop</button>
              )}
              {phase === "evaluated" && scoreSampling && (
                <span className="canvas__hint mono">Confirming score…</span>
              )}
              {phase === "evaluated" && evalParsed && !scoreSampling && pushbackPass === 3 && evalParsed.score < 4 && (
                <>
                  <button className="btn btn--ghost" onClick={handleRefine}>Revise one more time</button>
                  <button className="btn btn--warn" onClick={() => void handleAcceptAsGap()}>Accept as flagged gap →</button>
                </>
              )}
              {phase === "evaluated" && evalParsed && !scoreSampling && !(pushbackPass === 3 && evalParsed.score < 4) && (
                <>
                  <button className="btn btn--ghost" onClick={handleRefine}>Refine answer</button>
                  <button
                    className="btn btn--primary"
                    onClick={() => void handleSaveAndNext(true)}
                  >
                    Save &amp; next →
                  </button>
                </>
              )}
              {phase === "saving" && <button className="btn btn--primary" disabled>Saving…</button>}
            </footer>
          </>
        )}
        {showSkipCta && phase !== "phaseSummary" && (
          <section className="canvas__panel canvas__skip-cta">
            <p className="canvas__eyebrow">SOLO OPERATOR — Phase 0 complexity rule</p>
            <h2 className="canvas__question">You're running this canvas as a solo operator.</h2>
            <p className="canvas__lede">
              The "Human team" question doesn't apply when there's no team to describe — it's just you. Skip it and move to the next field.
            </p>
            <footer className="canvas__actions">
              <button className="btn btn--primary btn--large" disabled={phase === "saving"} onClick={() => void handleSkip()}>
                {phase === "saving" ? "Skipping…" : "Skip this field →"}
              </button>
            </footer>
          </section>
        )}
      </div>
    </div>
  );
}

// ── Evaluation card ──────────────────────────────────────────────────────────

function EvaluationCard({
  text,
  streaming,
  parsed,
}: {
  text: string;
  streaming: boolean;
  parsed: ReturnType<typeof parseEvalTail> | null;
}) {
  return (
    <section className="eval">
      <header className="eval__header">
        <span className="eval__label">Coach evaluation</span>
        {parsed && (
          <span className={`eval__score eval__score--${gradeOf(parsed.score)}`}>
            <span className="mono">{parsed.score}</span>
            <span className="eval__score-of">/5</span>
          </span>
        )}
      </header>
      <p className="eval__text">
        {renderProse(text)}
        {streaming && <span className="caret" />}
      </p>
      {parsed && (
        <div className="eval__chips">
          <span className="chip chip--neutral">
            <span className="chip__k">Confidence</span>
            <span className="chip__v mono">{parsed.confidence}</span>
          </span>
          {parsed.gapFlagged ? (
            <span className="chip chip--warn">
              <span className="chip__k">Gap</span>
              <span className="chip__v">{parsed.gapNote || "Flagged"}</span>
            </span>
          ) : (
            <span className="chip chip--ok">
              <span className="chip__k">Gap</span>
              <span className="chip__v">None</span>
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function gradeOf(score: number): "low" | "mid" | "high" {
  return score >= 4 ? "high" : score >= 3 ? "mid" : "low";
}

// ── DR Flow ───────────────────────────────────────────────────────────────────

function DRFlow({
  session,
  sessionId,
  onAdvance,
  onHome,
}: {
  session: CanvasSession;
  sessionId: string;
  onAdvance: () => Promise<void>;
  onHome?: () => void;
}) {
  const scoringModel = useScoringModel();
  const drIdx = session.currentFieldIndex;
  const dr = DR_QUESTIONS[drIdx];

  const [draft, setDraft] = useState("");
  const [streamText, setStreamText] = useState("");
  const [evalParsed, setEvalParsed] = useState<ReturnType<typeof parseEvalTail>>(null);
  const [phase, setPhase] = useState<Phase>("ask");
  const [error, setError] = useState<string | null>(null);
  const [pushbackPass, setPushbackPass] = useState(0);
  const [scoreSampling, setScoreSampling] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const total = DR_QUESTIONS.length;

  // Reset pushbackPass when DR field changes
  useEffect(() => {
    setPushbackPass(0);
  }, [drIdx]);

  const startStream = async () => {
    if (!draft.trim()) return;
    const system = buildDRSystemPrompt({ drCode: dr.code, drQuestion: dr.question, session, pushbackPass });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStreamText("");
    setEvalParsed(null);
    setScoreSampling(false);
    setPhase("streaming");

    const drMessages: ChatMessage[] = [
      {
        role: "user",
        content: `Step: ${dr.code}\nUser answer:\n"""\n${draft.trim()}\n"""`,
      },
    ];

    let acc = "";
    streamChat({
      url: chatUrl(),
      sessionId,
      system,
      messages: drMessages,
      provider: "anthropic",
      model: scoringModel,
      signal: ctrl.signal,
      onText: (delta) => {
        acc += delta;
        const idx = acc.indexOf("<<<JSON>>>");
        setStreamText(idx >= 0 ? acc.slice(0, idx) : acc);
      },
      onDone: () => {
        const parsed = parseEvalTail(acc);
        if (!parsed) {
          setError("Could not parse evaluation.");
          setPhase("ask");
          return;
        }
        setStreamText(parsed.proseText);
        setEvalParsed(parsed);
        setPhase("evaluated");
        if (SCORING_SAMPLES > 1) {
          setScoreSampling(true);
          void medianScore(parsed.score, {
            url: chatUrl(),
            sessionId,
            system,
            messages: drMessages,
            model: scoringModel,
            signal: ctrl.signal,
          }).then(({ score }) => {
            setScoreSampling(false);
            setEvalParsed({ ...parsed, score });
            if (score < 4) setPushbackPass((p) => Math.min(3, p + 1));
          });
        } else if (parsed.score < 4) {
          setPushbackPass((p) => Math.min(3, p + 1));
        }
      },
      onError: (e) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setPhase("ask");
      },
    });
  };

  const save = async () => {
    if (!evalParsed) return;
    setPhase("saving");
    try {
      await submitDR(sessionId, {
        drIndex: drIdx,
        answer: draft.trim(),
        evalText: evalParsed.proseText,
        score: evalParsed.score,
        gapFlagged: evalParsed.gapFlagged,
        gapNote: evalParsed.gapNote,
        confidence: evalParsed.confidence,
        advance: true,
        scoringModel,
      });
      setDraft("");
      setStreamText("");
      setEvalParsed(null);
      setPhase("ask");
      await onAdvance();
    } catch (e) {
      setError((e as Error).message);
      setPhase("evaluated");
    }
  };

  return (
    <div className="canvas">
      <PhaseTracker session={session} />
      <div className="canvas__panel">
        <header className="canvas__header">
          <div className="canvas__meta">
            <span className="canvas__phase-code">DR</span>
            <span className="canvas__phase-title">— Deployment Readiness</span>
          </div>
          <div className="canvas__progress">
            <span className="mono">{dr.code}</span>
            <span className="canvas__progress-sep">·</span>
            <span className="mono">{drIdx + 1}/{total}</span>
            {onHome && (
              <>
                <span className="canvas__progress-sep">·</span>
                <button className="btn btn--text" onClick={onHome}>← Home</button>
              </>
            )}
          </div>
        </header>

        <p className="canvas__eyebrow">{dr.code}</p>
        <h1 className="canvas__display">{renderProse(dr.question)}</h1>

        <div className="composer">
          <label className="composer__label">Your answer</label>
          <textarea
            className="composer__textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={phase === "streaming" || phase === "saving"}
            rows={6}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void startStream();
              }
            }}
          />
          <div className="composer__hint">
            <span>Cmd/Ctrl + Enter to submit</span>
          </div>
        </div>

        {(phase === "streaming" || phase === "evaluated") && (
          <EvaluationCard
            text={streamText}
            streaming={phase === "streaming"}
            parsed={evalParsed}
          />
        )}
        {error && <div className="banner banner--error">{error}</div>}

        <footer className="canvas__actions">
          {phase === "ask" && (
            <button
              className="btn btn--primary"
              disabled={!draft.trim()}
              onClick={() => void startStream()}
            >
              Submit for evaluation →
            </button>
          )}
          {phase === "streaming" && (
            <button className="btn btn--ghost" onClick={() => abortRef.current?.abort()}>Stop</button>
          )}
          {phase === "evaluated" && scoreSampling && (
            <span className="canvas__hint mono">Confirming score…</span>
          )}
          {phase === "evaluated" && !scoreSampling && (
            <>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setStreamText("");
                  setEvalParsed(null);
                  setPhase("ask");
                }}
              >
                Refine
              </button>
              <button className="btn btn--primary" onClick={() => void save()}>
                Save &amp; next →
              </button>
            </>
          )}
          {phase === "saving" && <button className="btn btn--primary" disabled>Saving…</button>}
        </footer>
      </div>
    </div>
  );
}

function Glyph() {
  return <span className="glyph" aria-hidden />;
}

// Silence the unused-import warning for items only present for clarity
void [PHASE_ORDER, PHASES, useMemo];
